import {
  type QueryClient,
  queryOptions,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'
import axios from 'axios'
import {
  type CircuitBreaker,
  createCircuitBreaker,
  type EntityPusherSyncConfig,
} from 'src/queries/pusherEntitySync'
import {
  createRateLimiter,
  type RateLimiter,
} from 'src/queries/pusherRateLimiter'

import * as network from './payments.network'
import {
  AccountCollectionFlowCompletionResponse,
  AccountCollectionFlowResponse,
  BillPaymentAuthorizationChallengeResponse,
  BillPaymentDetailsResponse,
  BillPaymentError,
  BillPaymentInvoiceAttachmentResponse,
  BillPaymentResponse,
  CancelBillPaymentAttempt,
  CompleteFundingAccountVerificationRequest,
  ConfirmBillPaymentAttempt,
  CreatePaymentConnectionRequest,
  CreatePaymentConnectionResponse,
  FundingAccountVerificationResponse,
  PaymentConnectionResponse,
  PaymentFundingAccountDetails,
  ResendBillPaymentAuthorizationAttempt,
  RestartBillPaymentAuthorizationAttempt,
  ScheduleBillPaymentAttempt,
  ScheduleBillPaymentError,
  UploadBillPaymentInvoiceAttachmentAttempt,
} from './payments.types'

const PAYMENTS_QK = 'payments'

/**
 * Colocated query-key factory. The `connection` sub-resource exposes a named
 * family prefix (`connections()`) plus an org-scoped exact key
 * (`connection(orgId)`).
 *
 * The org id is part of every exact key because logout does NOT clear the React
 * Query cache (see `src/queries/AGENTS.md`): an org-global key would let org A's
 * cached connection render for org B after a switch/login. Scoping by org id
 * keeps each org's server state in its own cache entry.
 *
 * A mutation still invalidates a single family (never `paymentsKeys.all`, which
 * would refetch every payments query at once — the refetch-storm the per-family
 * keys guard against).
 */
export const paymentsKeys = {
  all: [PAYMENTS_QK] as const,
  connections: () => [...paymentsKeys.all, 'connection'] as const,
  connection: (organizationId: string | undefined) =>
    [...paymentsKeys.connections(), organizationId] as const,
  fundingAccounts: () => [...paymentsKeys.all, 'funding-account'] as const,
  fundingAccount: (organizationId: string | undefined) =>
    [...paymentsKeys.fundingAccounts(), organizationId] as const,
  paymentDetails: () => [...paymentsKeys.all, 'payment-detail'] as const,
  paymentDetail: (
    organizationId: string | undefined,
    paymentId: string | undefined,
  ) => [...paymentsKeys.paymentDetails(), organizationId, paymentId] as const,
  invoiceAttachments: () =>
    [...paymentsKeys.all, 'invoice-attachment'] as const,
  invoiceAttachment: (
    organizationId: string | undefined,
    attachmentId: string | undefined,
  ) =>
    [...paymentsKeys.invoiceAttachments(), organizationId, attachmentId] as const,
}

const markPaymentConnectionsStale = (queryClient: QueryClient): void => {
  void queryClient.invalidateQueries({
    queryKey: paymentsKeys.connections(),
    refetchType: 'none',
  })
}

const refreshFundingAccount = (
  queryClient: QueryClient,
  organizationId: string | undefined,
): void => {
  if (!organizationId) return
  void queryClient.invalidateQueries(
    {
      queryKey: paymentsKeys.fundingAccount(organizationId),
    },
    { cancelRefetch: false },
  )
}

const isRateLimitError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 429

// One shared budget and breaker for PAYMENT_CONNECTION events across every
// Pusher reconnect in this browser session.
const paymentConnectionLimiter = createRateLimiter()
const paymentConnectionBreaker = createCircuitBreaker()

/**
 * Guard PAYMENT_CONNECTION Pusher refreshes behind the repository-standard
 * rate limiter and circuit breaker. UPDATED fetches once and patches the exact
 * active-org key; CREATED/overflow/429 merely mark the family stale with
 * `refetchType: 'none'` so bursts cannot amplify into unbounded API traffic.
 */
export const createPaymentConnectionPusherSync = ({
  queryClient,
  organizationId,
  limiter = paymentConnectionLimiter,
  breaker = paymentConnectionBreaker,
}: {
  queryClient: QueryClient
  organizationId: string | undefined
  limiter?: RateLimiter
  breaker?: CircuitBreaker
}): EntityPusherSyncConfig<PaymentConnectionResponse> => ({
  limiter,
  breaker,
  fetchDetail: async () =>
    organizationId ? network.getPaymentConnection() : undefined,
  applyUpdate: (connection) => {
    if (!organizationId) return
    queryClient.setQueryData(
      paymentsKeys.connection(organizationId),
      connection,
    )
    if (
      'fundingAccount' in connection &&
      Boolean(connection.fundingAccount)
    ) {
      refreshFundingAccount(queryClient, organizationId)
    }
  },
  removeFromLists: () => {
    if (!organizationId) return
    queryClient.removeQueries({
      queryKey: paymentsKeys.connection(organizationId),
      exact: true,
    })
  },
  onCreate: () => markPaymentConnectionsStale(queryClient),
  onOverflow: () => markPaymentConnectionsStale(queryClient),
  isRateLimitError,
})

// ─── Query factories ─────────────────────────────────────────────
// Consume directly: `useQuery(paymentsQueries.connection(organizationId))`.
// The org id scopes the cache key only — the endpoint derives the org from the
// session, so the network fns take no id. `enabled` guards a keyless fetch.

export const paymentsQueries = {
  connection: (organizationId: string | undefined) =>
    queryOptions({
      queryKey: paymentsKeys.connection(organizationId),
      queryFn: () => network.getPaymentConnection(),
      enabled: Boolean(organizationId),
    }),
  fundingAccount: (organizationId: string | undefined) =>
    queryOptions({
      queryKey: paymentsKeys.fundingAccount(organizationId),
      queryFn: () => network.getFundingAccount(),
      enabled: Boolean(organizationId),
      staleTime: 30_000,
    }),
  paymentDetail: (
    organizationId: string | undefined,
    paymentId: string | undefined,
  ) =>
    queryOptions({
      queryKey: paymentsKeys.paymentDetail(organizationId, paymentId),
      queryFn: () => {
        if (!paymentId) {
          throw new Error('paymentDetail requires a paymentId')
        }
        return network.getBillPayment(paymentId)
      },
      enabled: Boolean(organizationId && paymentId),
    }),
  invoiceAttachment: (
    organizationId: string | undefined,
    attachmentId: string | undefined,
  ) =>
    queryOptions({
      queryKey: paymentsKeys.invoiceAttachment(organizationId, attachmentId),
      queryFn: () => {
        if (!attachmentId) {
          throw new Error('invoiceAttachment requires an attachmentId')
        }
        return network.getBillPaymentInvoiceAttachment(attachmentId)
      },
      enabled: Boolean(organizationId && attachmentId),
    }),
}

/**
 * Submit the legal-entity (KYB) details — `POST /payments/connection`, now
 * KYB-only (the split flow). Narrow invalidation: the connection summary only
 * (its `onboardingStatus` flips NOT_CONNECTED → KYB_PENDING / _ACTIVE). The
 * account list is untouched — no bank account is created here.
 *
 * PII HANDLING: the request `variables` (EIN, SSNs, DOBs, beneficial-owner
 * identities, base64 document bytes) are held by React Query's MutationCache for
 * the mutation's lifetime. `gcTime: 0` evicts the settled mutation — and its
 * retained `variables` — the instant it goes inactive (no observers), rather
 * than lingering for the default 5-minute GC window after the modal closes. The
 * page additionally `reset()`s the observer on modal close to scrub the live
 * copy. Only `legalEntityId` + `kybStatus` (+ legalBusinessName / legalAddress)
 * ever land in the query cache — never the KYB PII.
 */
export function useSubmitLegalEntity(
  organizationId: string | undefined,
  options?: UseMutationOptions<
    CreatePaymentConnectionResponse,
    Error,
    CreatePaymentConnectionRequest
  >,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    // Do not retain the KYB PII variables past settle — evict on inactive.
    // Keep this after caller options so the privacy invariant cannot be
    // accidentally weakened by a shared/default mutation option.
    gcTime: 0,
    mutationFn: (request) => network.submitLegalEntity(request),
    onSuccess: async (data, variables, onMutateResult, context) => {
      // Await the refetch so `mutateAsync` (and the modal's close-on-resolve)
      // does not settle until the connection carrying the new `onboardingStatus`
      // is in cache. Otherwise the modal closes over a stale NOT_CONNECTED /
      // `fundingAccount=null` and re-exposes the "Add bank account" flow →
      // duplicate onboarding (EX-40413 review fix). React Query awaits a promise
      // returned from `onSuccess` before the mutation settles.
      await queryClient.invalidateQueries({
        queryKey: paymentsKeys.connection(organizationId),
      })
      await onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

/**
 * Bootstrap the embedded Modern Treasury Account Collection Flow for the
 * org's own funding account (`POST /payments/account-collection-flow`). The
 * mutation itself does NOT invalidate anything — the flow is not complete
 * until MT's `onSuccess` fires client-side and the app links the account via
 * `useCompleteAccountCollectionFlow`, which owns the connection invalidation.
 */
export function useCreateAccountCollectionFlow(
  options?: UseMutationOptions<AccountCollectionFlowResponse, Error, void>,
) {
  return useMutation({
    ...options,
    mutationFn: () => network.createAccountCollectionFlow(),
  })
}

/**
 * Complete the embedded flow — `POST /payments/account-collection-flow/{flowId}/
 * complete` — from the MT embed's `onSuccess` (MT fires no webhook). On success
 * (200 `COMPLETED` or 202 `PENDING`) invalidate the connection family: a 200
 * links the funding account, so the connection refetch now carries
 * `fundingAccount` and the Bank Account card flips to "connected". A 202 leaves
 * the account unlinked for now; the invalidation is cheap and keeps the surface
 * eventually consistent. 4xx/5xx (404/409/502) reject through the mutation's
 * `Error` channel so the caller can surface an error / allow restart.
 *
 * The mutation variable is the `flowId` returned by
 * `useCreateAccountCollectionFlow`. Narrow invalidation by design — never
 * `paymentsKeys.all`.
 */
export function useCompleteAccountCollectionFlow(
  organizationId: string | undefined,
  options?: UseMutationOptions<
    AccountCollectionFlowCompletionResponse,
    Error,
    string
  >,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    mutationFn: (flowId) => network.completeAccountCollectionFlow(flowId),
    onSuccess: async (data, variables, onMutateResult, context) => {
      if (data.status === 'COMPLETED' && data.fundingAccount) {
        queryClient.setQueryData<PaymentConnectionResponse>(
          paymentsKeys.connection(organizationId),
          (current) =>
            current && current.onboardingStatus !== 'NOT_CONNECTED'
              ? { ...current, fundingAccount: data.fundingAccount }
              : current,
        )
        refreshFundingAccount(queryClient, organizationId)
      }
      // Keep settlement immediate so the page can open verification without
      // waiting for a refetch; the optimistic patch above prevents CTA bounce.
      void queryClient.invalidateQueries({
        queryKey: paymentsKeys.connection(organizationId),
      })
      await onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export function useInitiateFundingAccountVerification(
  organizationId: string | undefined,
  options?: UseMutationOptions<FundingAccountVerificationResponse, Error, void>,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    mutationFn: () => network.initiateFundingAccountVerification(),
    onSuccess: async (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<PaymentConnectionResponse>(
        paymentsKeys.connection(organizationId),
        (current) =>
          current && current.onboardingStatus !== 'NOT_CONNECTED'
            ? { ...current, fundingAccount: data.fundingAccount }
            : current,
      )
      void queryClient.invalidateQueries({
        queryKey: paymentsKeys.connection(organizationId),
      })
      refreshFundingAccount(queryClient, organizationId)
      await onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export function useCompleteFundingAccountVerification(
  organizationId: string | undefined,
  options?: UseMutationOptions<
    FundingAccountVerificationResponse,
    Error,
    CompleteFundingAccountVerificationRequest
  >,
) {
  const queryClient = useQueryClient()
  const { onError, onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    // Do not retain submitted microdeposit amounts in the MutationCache.
    gcTime: 0,
    mutationFn: (request) =>
      network.completeFundingAccountVerification(request),
    onSuccess: async (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<PaymentConnectionResponse>(
        paymentsKeys.connection(organizationId),
        (current) =>
          current && current.onboardingStatus !== 'NOT_CONNECTED'
            ? { ...current, fundingAccount: data.fundingAccount }
            : current,
      )
      void queryClient.invalidateQueries({
        queryKey: paymentsKeys.connection(organizationId),
      })
      refreshFundingAccount(queryClient, organizationId)
      await onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: async (error, variables, onMutateResult, context) => {
      if (
        error &&
        typeof error === 'object' &&
        'code' in error &&
        String(error.code).toUpperCase() ===
          'VERIFICATION_ACTION_REQUIRED'
      ) {
        void queryClient.invalidateQueries({
          queryKey: paymentsKeys.connection(organizationId),
        })
        refreshFundingAccount(queryClient, organizationId)
      }
      await onError?.(error, variables, onMutateResult, context)
    },
  })
}

export function useScheduleBillPayment(
  options?: UseMutationOptions<
    BillPaymentResponse,
    ScheduleBillPaymentError,
    ScheduleBillPaymentAttempt
  >,
) {
  return useMutation({
    ...options,
    // OTP variables must not remain in the MutationCache after the observer
    // unmounts, and callers cannot weaken that retention policy.
    gcTime: 0,
    mutationFn: (attempt) => network.scheduleBillPayment(attempt),
  })
}

export function useUploadBillPaymentInvoiceAttachment(
  options?: UseMutationOptions<
    BillPaymentInvoiceAttachmentResponse,
    BillPaymentError,
    UploadBillPaymentInvoiceAttachmentAttempt
  >,
) {
  return useMutation({
    ...options,
    gcTime: 0,
    mutationFn: (attempt) => network.uploadBillPaymentInvoiceAttachment(attempt),
  })
}

export function useDeleteBillPaymentInvoiceAttachment(
  organizationId: string | undefined,
  options?: UseMutationOptions<void, BillPaymentError, string>,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    mutationFn: (attachmentId) =>
      network.deleteBillPaymentInvoiceAttachment(attachmentId),
    onSuccess: async (data, attachmentId, onMutateResult, context) => {
      queryClient.removeQueries({
        queryKey: paymentsKeys.invoiceAttachment(
          organizationId,
          attachmentId,
        ),
        exact: true,
      })
      await onSuccess?.(data, attachmentId, onMutateResult, context)
    },
  })
}

const setBillPaymentDetails = (
  queryClient: QueryClient,
  organizationId: string | undefined,
  details: BillPaymentDetailsResponse,
): void => {
  queryClient.setQueryData(
    paymentsKeys.paymentDetail(organizationId, details.payment.id),
    details,
  )
}

export function useConfirmBillPayment(
  organizationId: string | undefined,
  options?: UseMutationOptions<
    BillPaymentDetailsResponse,
    BillPaymentError,
    ConfirmBillPaymentAttempt
  >,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    gcTime: 0,
    mutationFn: (attempt) => network.confirmBillPayment(attempt),
    onSuccess: async (data, variables, onMutateResult, context) => {
      setBillPaymentDetails(queryClient, organizationId, data)
      await onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export function useRestartBillPaymentAuthorization(
  organizationId: string | undefined,
  options?: UseMutationOptions<
    BillPaymentDetailsResponse,
    BillPaymentError,
    RestartBillPaymentAuthorizationAttempt
  >,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    mutationFn: (attempt) => network.restartBillPaymentAuthorization(attempt),
    onSuccess: async (data, variables, onMutateResult, context) => {
      setBillPaymentDetails(queryClient, organizationId, data)
      await onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export function useResendBillPaymentAuthorization(
  organizationId: string | undefined,
  options?: UseMutationOptions<
    BillPaymentDetailsResponse,
    BillPaymentError,
    ResendBillPaymentAuthorizationAttempt
  >,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    mutationFn: (attempt) => network.resendBillPaymentAuthorization(attempt),
    onSuccess: async (data, variables, onMutateResult, context) => {
      setBillPaymentDetails(queryClient, organizationId, data)
      await onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

export function useCancelBillPayment(
  organizationId: string | undefined,
  options?: UseMutationOptions<
    BillPaymentResponse,
    BillPaymentError,
    CancelBillPaymentAttempt
  >,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...rest } = options ?? {}
  return useMutation({
    ...rest,
    mutationFn: (attempt) => network.cancelBillPayment(attempt),
    onSuccess: async (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<BillPaymentDetailsResponse>(
        paymentsKeys.paymentDetail(organizationId, data.id),
        (current) =>
          current ? { payment: data, authorization: null } : current,
      )
      await onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

// Re-export response/list types consumers commonly destructure off factory
// results, mirroring the payables module's export shape.
export type {
  AccountCollectionFlowCompletionResponse,
  AccountCollectionFlowResponse,
  BillPaymentAuthorizationChallengeResponse,
  BillPaymentDetailsResponse,
  BillPaymentError,
  BillPaymentInvoiceAttachmentResponse,
  BillPaymentResponse,
  CancelBillPaymentAttempt,
  CompleteFundingAccountVerificationRequest,
  ConfirmBillPaymentAttempt,
  FundingAccountVerificationResponse,
  PaymentConnectionResponse,
  PaymentFundingAccountDetails,
  ResendBillPaymentAuthorizationAttempt,
  RestartBillPaymentAuthorizationAttempt,
  ScheduleBillPaymentAttempt,
  ScheduleBillPaymentError,
  UploadBillPaymentInvoiceAttachmentAttempt,
}
